import React, { useState, useEffect, useMemo } from "react";
import { iconNames } from "@/constants/iconNames";
import iconMap from "@/components/icons/iconMap";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ChevronDown } from "lucide-react";
// DynamicLucideIcon removed for reliability and to avoid mixed static/dynamic imports

// Simple interface for IconSelectorProps
interface IconSelectorProps {
  value: string;
  onChange: (iconName: string) => void;
  placeholder?: string;
}

export const IconSelector = React.memo(({ value, onChange, placeholder = "Select an icon" }: IconSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredIconNames = useMemo(() => {
    if (!searchTerm) return iconNames;
    return iconNames.filter(name =>
      name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const handleSelectIcon = (iconName: string) => {
    onChange(iconName);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal"
          type="button"
        >
          <div className="flex items-center gap-2 flex-1">
            {value ? (
              (() => {
                const Icon = iconMap[value as keyof typeof iconMap];
                return (
                  <>
                    {Icon ? <Icon className="h-4 w-4" /> : <span className="text-[10px]">?</span>}
                    <span>{value}</span>
                  </>
                );
              })()
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search icons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-3">
          {filteredIconNames.length > 0 ? (
            <div className="grid grid-cols-6 gap-2">
              {filteredIconNames.map(iconName => {
                const Icon = iconMap[iconName as keyof typeof iconMap];
                return (
                  <Button
                    key={iconName}
                    variant={value === iconName ? "default" : "ghost"}
                    size="sm"
                    className="h-10 w-10 p-0"
                    onClick={() => handleSelectIcon(iconName)}
                    type="button"
                    title={iconName}
                  >
                    {Icon ? <Icon className="h-5 w-5" /> : <span className="text-[10px]">?</span>}
                  </Button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-muted-foreground">
              No icons found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
});
